import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Slack, Github, Plus, Trash2, CheckCircle2, AlertCircle, Loader2, RefreshCw, Users, Puzzle, ListChecks } from 'lucide-react'
import { apiClient } from '../services/api'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import type { ApiErrorLike } from '../utils/apiError'
import { projectsService } from '../services/projects'

interface Integration {
  id: number
  type: string
  name: string
  is_active: boolean
  status?: string
  project_id?: number
  organization_id?: number
  last_sync_at?: string
  created_at: string
  updated_at: string
}

interface SlackFormState {
  name: string
  bot_token: string
  channel_id: string
  channel_name: string
  notify_task_assigned: boolean
  notify_milestone_complete: boolean
  notify_ai_insight: boolean
  notify_change_request_approved: boolean
  project_id: string
}

interface GitHubFormState {
  name: string
  repo_owner: string
  repo_name: string
  webhook_secret: string
  auto_close_on_merge: boolean
  project_id: string
}

interface TeamsFormState {
  name: string
  webhook_url: string
  notify_task_assigned: boolean
  notify_milestone_complete: boolean
  notify_ai_insight: boolean
  project_id: string
}

interface JiraFormState {
  name: string
  jira_url: string
  email: string
  api_token: string
  project_key: string
}

interface AsanaFormState {
  name: string
  access_token: string
  project_gid: string
}

const defaultAsana: AsanaFormState = {
  name: 'Asana',
  access_token: '',
  project_gid: '',
}

const defaultSlack: SlackFormState = {
  name: 'Slack',
  bot_token: '',
  channel_id: '',
  channel_name: '',
  notify_task_assigned: true,
  notify_milestone_complete: true,
  notify_ai_insight: true,
  notify_change_request_approved: true,
  project_id: '',
}

const defaultGitHub: GitHubFormState = {
  name: 'GitHub',
  repo_owner: '',
  repo_name: '',
  webhook_secret: '',
  auto_close_on_merge: true,
  project_id: '',
}

const defaultTeams: TeamsFormState = {
  name: 'Microsoft Teams',
  webhook_url: '',
  notify_task_assigned: true,
  notify_milestone_complete: true,
  notify_ai_insight: true,
  project_id: '',
}

const defaultJira: JiraFormState = {
  name: 'Jira',
  jira_url: '',
  email: '',
  api_token: '',
  project_key: '',
}

export default function IntegrationsSettings() {
  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [showSlackDialog, setShowSlackDialog] = useState(false)
  const [showGitHubDialog, setShowGitHubDialog] = useState(false)
  const [showTeamsDialog, setShowTeamsDialog] = useState(false)
  const [showJiraDialog, setShowJiraDialog] = useState(false)
  const [slackForm, setSlackForm] = useState<SlackFormState>(defaultSlack)
  const [githubForm, setGitHubForm] = useState<GitHubFormState>(defaultGitHub)
  const [teamsForm, setTeamsForm] = useState<TeamsFormState>(defaultTeams)
  const [jiraForm, setJiraForm] = useState<JiraFormState>(defaultJira)
  const [showAsanaDialog, setShowAsanaDialog] = useState(false)
  const [asanaForm, setAsanaForm] = useState<AsanaFormState>(defaultAsana)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState<number | null>(null)
  const [importing, setImporting] = useState<number | null>(null)
  // Two-way task sync (2026-09-27): which Karyva project each Jira
  // integration mirrors into.
  const [syncProjects, setSyncProjects] = useState<Array<{ id: number; name: string }>>([])
  const [syncTarget, setSyncTarget] = useState<Record<number, string>>({})
  useEffect(() => {
    projectsService.list({ limit: 200 })
      .then((ps) => setSyncProjects(ps.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => setSyncProjects([]))
  }, [])
  const [searchParams] = useSearchParams()
  const VALID_TABS = ['slack', 'teams', 'jira', 'asana', 'github']
  const requestedTab = searchParams.get('tab')
  const initialTab = requestedTab && VALID_TABS.includes(requestedTab) ? requestedTab : 'slack'

  useEffect(() => { load() }, [])

  // Surface the connection-test result that the API ran on save. `status`
  // comes back "active" when the probe succeeded, "error" when it failed.
  function reportConnectionResult(label: string, data: Integration) {
    if (data.status === 'error') {
      toast.warning(`${label} saved, but the connection test failed. Re-check the credentials and use "Test".`)
    } else {
      toast.success(`${label} connected — connection test passed`)
    }
  }

  async function load() {
    setLoading(true)
    try {
      const { data } = await apiClient.get<Integration[]>('/api/v1/integrations/')
      setIntegrations(data)
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status === 403) setForbidden(true)
      else toast.error('Failed to load integrations')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateSlack() {
    if (!slackForm.bot_token || !slackForm.channel_id) {
      toast.error('Bot token and channel ID are required')
      return
    }
    setSaving(true)
    try {
      const { data } = await apiClient.post<Integration>('/api/v1/integrations/?test_connection=true', {
        type: 'slack',
        name: slackForm.name || 'Slack',
        project_id: slackForm.project_id ? Number(slackForm.project_id) : null,
        config: {
          bot_token: slackForm.bot_token,
          channel_id: slackForm.channel_id,
          channel_name: slackForm.channel_name,
          notify_task_assigned: slackForm.notify_task_assigned,
          notify_milestone_complete: slackForm.notify_milestone_complete,
          notify_ai_insight: slackForm.notify_ai_insight,
          notify_change_request_approved: slackForm.notify_change_request_approved,
        },
      })
      setIntegrations(prev => [data, ...prev])
      setShowSlackDialog(false)
      setSlackForm(defaultSlack)
      reportConnectionResult('Slack', data)
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to add integration')
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateGitHub() {
    if (!githubForm.repo_owner || !githubForm.repo_name || !githubForm.webhook_secret) {
      toast.error('Repository owner, name, and webhook secret are required')
      return
    }
    setSaving(true)
    try {
      const { data } = await apiClient.post<Integration>('/api/v1/integrations/?test_connection=true', {
        type: 'github',
        name: githubForm.name || `${githubForm.repo_owner}/${githubForm.repo_name}`,
        project_id: githubForm.project_id ? Number(githubForm.project_id) : null,
        config: {
          repo_owner: githubForm.repo_owner,
          repo_name: githubForm.repo_name,
          webhook_secret: githubForm.webhook_secret,
          auto_close_on_merge: githubForm.auto_close_on_merge,
        },
      })
      setIntegrations(prev => [data, ...prev])
      setShowGitHubDialog(false)
      setGitHubForm(defaultGitHub)
      reportConnectionResult('GitHub', data)
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to add integration')
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateTeams() {
    if (!teamsForm.webhook_url) {
      toast.error('Incoming webhook URL is required')
      return
    }
    if (!teamsForm.webhook_url.startsWith('https://')) {
      toast.error('Webhook URL must start with https://')
      return
    }
    setSaving(true)
    try {
      const { data } = await apiClient.post<Integration>('/api/v1/integrations/?test_connection=true', {
        type: 'teams',
        name: teamsForm.name || 'Microsoft Teams',
        project_id: teamsForm.project_id ? Number(teamsForm.project_id) : null,
        config: {
          webhook_url: teamsForm.webhook_url,
          notify_task_assigned: teamsForm.notify_task_assigned,
          notify_milestone_complete: teamsForm.notify_milestone_complete,
          notify_ai_insight: teamsForm.notify_ai_insight,
        },
      })
      setIntegrations(prev => [data, ...prev])
      setShowTeamsDialog(false)
      setTeamsForm(defaultTeams)
      reportConnectionResult('Microsoft Teams', data)
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to add integration')
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateJira() {
    if (!jiraForm.jira_url || !jiraForm.email || !jiraForm.api_token) {
      toast.error('Jira URL, email, and API token are required')
      return
    }
    setSaving(true)
    try {
      const { data } = await apiClient.post<Integration>('/api/v1/integrations/?test_connection=true', {
        type: 'jira',
        name: jiraForm.name || 'Jira',
        config: {
          base_url: jiraForm.jira_url.replace(/\/$/, ''),
          jira_url: jiraForm.jira_url.replace(/\/$/, ''),
          email: jiraForm.email,
          api_token: jiraForm.api_token,
          project_key: jiraForm.project_key,
        },
      })
      setIntegrations(prev => [data, ...prev])
      setShowJiraDialog(false)
      setJiraForm(defaultJira)
      reportConnectionResult('Jira', data)
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to add integration')
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateAsana() {
    if (!asanaForm.access_token || !asanaForm.project_gid) {
      toast.error('Asana access token and project ID are required')
      return
    }
    setSaving(true)
    try {
      const { data } = await apiClient.post<Integration>('/api/v1/integrations/?test_connection=true', {
        type: 'asana',
        name: asanaForm.name || 'Asana',
        config: {
          access_token: asanaForm.access_token.trim(),
          project_gid: asanaForm.project_gid.trim(),
        },
      })
      setIntegrations(prev => [data, ...prev])
      setShowAsanaDialog(false)
      setAsanaForm(defaultAsana)
      reportConnectionResult('Asana', data)
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Failed to add integration')
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleActive(intg: Integration) {
    try {
      const { data } = await apiClient.patch<Integration>(`/api/v1/integrations/${intg.id}`, {
        is_active: !intg.is_active,
      })
      setIntegrations(prev => prev.map(i => (i.id === data.id ? data : i)))
    } catch {
      toast.error('Failed to update integration')
    }
  }

  async function handleDelete(id: number) {
    try {
      await apiClient.delete(`/api/v1/integrations/${id}`)
      setIntegrations(prev => prev.filter(i => i.id !== id))
      toast.success('Integration removed')
    } catch {
      toast.error('Failed to remove integration')
    }
  }

  async function handleTestSlack(id: number) {
    setTesting(id)
    try {
      await apiClient.post(`/api/v1/integrations/${id}/slack/test`)
      toast.success('Test message sent to Slack')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Slack test failed')
    } finally {
      setTesting(null)
    }
  }

  async function handleTestTeams(id: number) {
    setTesting(id)
    try {
      await apiClient.post(`/api/v1/integrations/${id}/teams/test`)
      toast.success('Test message sent to Teams')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(err.response?.data?.detail || 'Teams test failed')
    } finally {
      setTesting(null)
    }
  }

  async function handleTaskSync(id: number, vendor: string) {
    const projectId = syncTarget[id]
    if (!projectId) {
      toast.error('Choose the Karyva project to sync with first.')
      return
    }
    setImporting(id)
    try {
      const { data } = await apiClient.post(`/api/v1/integrations/${id}/sync`, null, { params: { project_id: projectId } })
      toast.success(`Synced: ${data.created} new, ${data.updated_local} updated from ${vendor}, ${data.pushed} sent to ${vendor}`)
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Sync failed')
    } finally {
      setImporting(null)
    }
  }

  const slackIntegrations = integrations.filter(i => i.type === 'slack')
  const githubIntegrations = integrations.filter(i => i.type === 'github')
  const teamsIntegrations = integrations.filter(i => i.type === 'teams')
  const jiraIntegrations = integrations.filter(i => i.type === 'jira')
  const asanaIntegrations = integrations.filter(i => i.type === 'asana')

  const renderSyncControl = (intg: Integration, vendor: string) => (
    <div className="flex items-center gap-2">
      <select
        id={`${vendor.toLowerCase()}-sync-project-${intg.id}`}
        aria-label="Karyva project to sync with"
        value={syncTarget[intg.id] ?? ''}
        onChange={(e) => setSyncTarget((prev) => ({ ...prev, [intg.id]: e.target.value }))}
        className="min-h-[44px] rounded-md border border-border bg-background px-2 text-sm"
      >
        <option value="">Sync with project…</option>
        {syncProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleTaskSync(intg.id, vendor)}
        disabled={!intg.is_active || importing === intg.id}
        className="min-h-[44px]"
        aria-label={`Sync tasks with ${vendor}`}
      >
        {importing === intg.id
          ? <Loader2 className="size-3 animate-spin mr-1" aria-hidden />
          : <RefreshCw className="size-3 mr-1" aria-hidden />}
        Sync
      </Button>
    </div>
  )

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Integrations</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Connect Karyva to your existing tools.
        </p>
      </div>

      {forbidden ? (
        <p role="status" className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          Only workspace admins can connect or change integrations.
        </p>
      ) : (
      <Tabs defaultValue={initialTab}>
        <TabsList className="min-h-[44px]">
          <TabsTrigger value="slack" className="min-h-[44px] gap-2">
            <Slack className="size-4" aria-hidden /> Slack
          </TabsTrigger>
          <TabsTrigger value="teams" className="min-h-[44px] gap-2">
            <Users className="size-4" aria-hidden /> Teams
          </TabsTrigger>
          <TabsTrigger value="jira" className="min-h-[44px] gap-2">
            <Puzzle className="size-4" aria-hidden /> Jira
          </TabsTrigger>
          <TabsTrigger value="asana" className="min-h-[44px] gap-2">
            <ListChecks className="size-4" aria-hidden /> Asana
          </TabsTrigger>
          <TabsTrigger value="github" className="min-h-[44px] gap-2">
            <Github className="size-4" aria-hidden /> GitHub
          </TabsTrigger>
        </TabsList>

        {/* Slack Tab */}
        <TabsContent value="slack" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Slack className="size-5" aria-hidden /> Slack
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Post Karyva notifications to Slack channels. Run <code className="text-xs bg-muted px-1 rounded">/karyva status [project]</code> from Slack.
                    Tasks from "My Day" can also be exported directly to your Slack channel.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowSlackDialog(true)} className="shrink-0 min-h-[44px]">
                  <Plus className="size-4 mr-1" aria-hidden /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Loading…</span>
                </div>
              ) : slackIntegrations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No Slack integrations yet.</p>
              ) : (
                <ul className="space-y-3" aria-label="Slack integrations">
                  {slackIntegrations.map(intg => (
                    <IntegrationRow
                      key={intg.id}
                      integration={intg}
                      onToggle={() => handleToggleActive(intg)}
                      onDelete={() => handleDelete(intg.id)}
                      extraAction={
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestSlack(intg.id)}
                          disabled={!intg.is_active || testing === intg.id}
                          className="min-h-[44px]"
                          aria-label="Send test message to Slack"
                        >
                          {testing === intg.id
                            ? <Loader2 className="size-3 animate-spin mr-1" aria-hidden />
                            : <RefreshCw className="size-3 mr-1" aria-hidden />}
                          Test
                        </Button>
                      }
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="py-4">
              <p className="text-sm font-medium mb-1">Webhook endpoint (for Slack slash commands)</p>
              <code className="text-xs bg-muted px-2 py-1 rounded break-all">
                {window.location.origin}/api/v1/integrations/slack/events
              </code>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Teams Tab */}
        <TabsContent value="teams" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="size-5" aria-hidden /> Microsoft Teams
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Send Karyva notifications to Teams channels via incoming webhooks.
                    Create an incoming webhook in your Teams channel settings and paste the URL here.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowTeamsDialog(true)} className="shrink-0 min-h-[44px]">
                  <Plus className="size-4 mr-1" aria-hidden /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Loading…</span>
                </div>
              ) : teamsIntegrations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No Teams integrations yet.</p>
              ) : (
                <ul className="space-y-3" aria-label="Teams integrations">
                  {teamsIntegrations.map(intg => (
                    <IntegrationRow
                      key={intg.id}
                      integration={intg}
                      onToggle={() => handleToggleActive(intg)}
                      onDelete={() => handleDelete(intg.id)}
                      extraAction={
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestTeams(intg.id)}
                          disabled={!intg.is_active || testing === intg.id}
                          className="min-h-[44px]"
                          aria-label="Send test message to Teams"
                        >
                          {testing === intg.id
                            ? <Loader2 className="size-3 animate-spin mr-1" aria-hidden />
                            : <RefreshCw className="size-3 mr-1" aria-hidden />}
                          Test
                        </Button>
                      }
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="py-4 space-y-1">
              <p className="text-sm font-medium">How to get a Teams incoming webhook URL</p>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Open your Teams channel → click ••• → Connectors</li>
                <li>Search for "Incoming Webhook" and click Add</li>
                <li>Name it "Karyva", upload an icon, click Create</li>
                <li>Copy the webhook URL and paste it above</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Jira Tab */}
        <TabsContent value="jira" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Puzzle className="size-5" aria-hidden /> Jira
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Connect your Jira instance to import issues as Karyva tasks.
                    Uses Jira REST API with an API token for authentication.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowJiraDialog(true)} className="shrink-0 min-h-[44px]">
                  <Plus className="size-4 mr-1" aria-hidden /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Loading…</span>
                </div>
              ) : jiraIntegrations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No Jira integrations yet.</p>
              ) : (
                <ul className="space-y-3" aria-label="Jira integrations">
                  {jiraIntegrations.map(intg => (
                    <IntegrationRow
                      key={intg.id}
                      integration={intg}
                      onToggle={() => handleToggleActive(intg)}
                      onDelete={() => handleDelete(intg.id)}
                      extraAction={renderSyncControl(intg, 'Jira')}
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="py-4 space-y-1">
              <p className="text-sm font-medium">How to get a Jira API token</p>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Go to id.atlassian.com → Security → Create and manage API tokens</li>
                <li>Click "Create API token", give it a name, copy the token</li>
                <li>Use your Atlassian account email and the token above</li>
                <li>The project key is the prefix of your Jira issues (e.g. "PROJ" for PROJ-123)</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Asana Tab */}
        <TabsContent value="asana" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <ListChecks className="size-5" aria-hidden /> Asana
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Two-way task sync with an Asana project. New tasks on either side are
                    created on the other; the most recent edit wins.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowAsanaDialog(true)} className="shrink-0 min-h-[44px]">
                  <Plus className="size-4 mr-1" aria-hidden /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Loading…</span>
                </div>
              ) : asanaIntegrations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No Asana integrations yet.</p>
              ) : (
                <ul className="space-y-3" aria-label="Asana integrations">
                  {asanaIntegrations.map(intg => (
                    <IntegrationRow
                      key={intg.id}
                      integration={intg}
                      onToggle={() => handleToggleActive(intg)}
                      onDelete={() => handleDelete(intg.id)}
                      extraAction={renderSyncControl(intg, 'Asana')}
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="py-4 space-y-1">
              <p className="text-sm font-medium">How to connect Asana</p>
              <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>In Asana, open My settings → Apps → Developer apps → Create new token</li>
                <li>Copy the personal access token</li>
                <li>Open the Asana project; the project ID is the long number in its URL</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        {/* GitHub Tab */}
        <TabsContent value="github" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Github className="size-5" aria-hidden /> GitHub
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Link PRs to tasks by referencing <code className="text-xs bg-muted px-1 rounded">KARV-42</code> in the PR title.
                    Merged PRs auto-update task status.
                  </CardDescription>
                </div>
                <Button onClick={() => setShowGitHubDialog(true)} className="shrink-0 min-h-[44px]">
                  <Plus className="size-4 mr-1" aria-hidden /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  <span>Loading…</span>
                </div>
              ) : githubIntegrations.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">No GitHub integrations yet.</p>
              ) : (
                <ul className="space-y-3" aria-label="GitHub integrations">
                  {githubIntegrations.map(intg => (
                    <IntegrationRow
                      key={intg.id}
                      integration={intg}
                      onToggle={() => handleToggleActive(intg)}
                      onDelete={() => handleDelete(intg.id)}
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-dashed">
            <CardContent className="py-4 space-y-1">
              <p className="text-sm font-medium">Webhook URL (add in your GitHub repo settings)</p>
              <code className="text-xs bg-muted px-2 py-1 rounded break-all block">
                {window.location.origin}/api/v1/integrations/github/webhook
              </code>
              <p className="text-xs text-muted-foreground">
                Events: <code>pull_request</code> — set content type to <code>application/json</code>
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      )}

      {/* Slack dialog */}
      <Dialog open={showSlackDialog} onOpenChange={setShowSlackDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Slack className="size-5" aria-hidden /> Add Slack Integration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Name" id="slack-name">
              <Input id="slack-name" value={slackForm.name} onChange={e => setSlackForm(f => ({ ...f, name: e.target.value }))} className="min-h-[44px]" />
            </Field>
            <Field label="Bot Token" id="slack-token" hint="Starts with xoxb-">
              <Input id="slack-token" type="password" value={slackForm.bot_token} onChange={e => setSlackForm(f => ({ ...f, bot_token: e.target.value }))} placeholder="xoxb-..." className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Channel ID" id="slack-channel" hint="e.g. C0123ABCDEF">
              <Input id="slack-channel" value={slackForm.channel_id} onChange={e => setSlackForm(f => ({ ...f, channel_id: e.target.value }))} placeholder="C0123ABCDEF" className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Channel Name (display only)" id="slack-channel-name">
              <Input id="slack-channel-name" value={slackForm.channel_name} onChange={e => setSlackForm(f => ({ ...f, channel_name: e.target.value }))} placeholder="#project-updates" className="min-h-[44px]" />
            </Field>
            <Field label="Project ID (leave blank for org-wide)" id="slack-project">
              <Input id="slack-project" type="number" value={slackForm.project_id} onChange={e => setSlackForm(f => ({ ...f, project_id: e.target.value }))} placeholder="Optional" className="min-h-[44px]" />
            </Field>
            <div className="space-y-3 pt-1">
              <p className="text-sm font-medium">Notify on</p>
              {([
                ['notify_task_assigned', 'Task assigned'],
                ['notify_milestone_complete', 'Milestone complete'],
                ['notify_ai_insight', 'Critical AI insight'],
                ['notify_change_request_approved', 'Change request approved'],
              ] as [keyof SlackFormState, string][]).map(([key, label]) => (
                <div key={key} className="flex items-center gap-3">
                  <Switch id={`slack-${key}`} checked={Boolean(slackForm[key])} onCheckedChange={v => setSlackForm(f => ({ ...f, [key]: v }))} />
                  <Label htmlFor={`slack-${key}`}>{label}</Label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSlackDialog(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleCreateSlack} disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" aria-hidden />}
              Connect Slack
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teams dialog */}
      <Dialog open={showTeamsDialog} onOpenChange={setShowTeamsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="size-5" aria-hidden /> Add Microsoft Teams Integration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Name" id="teams-name">
              <Input id="teams-name" value={teamsForm.name} onChange={e => setTeamsForm(f => ({ ...f, name: e.target.value }))} className="min-h-[44px]" />
            </Field>
            <Field label="Incoming Webhook URL" id="teams-webhook" hint="From Teams channel → Connectors → Incoming Webhook">
              <Input id="teams-webhook" value={teamsForm.webhook_url} onChange={e => setTeamsForm(f => ({ ...f, webhook_url: e.target.value }))} placeholder="https://outlook.office.com/webhook/..." className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Project ID (leave blank for org-wide)" id="teams-project">
              <Input id="teams-project" type="number" value={teamsForm.project_id} onChange={e => setTeamsForm(f => ({ ...f, project_id: e.target.value }))} placeholder="Optional" className="min-h-[44px]" />
            </Field>
            <div className="space-y-3 pt-1">
              <p className="text-sm font-medium">Notify on</p>
              {([
                ['notify_task_assigned', 'Task assigned'],
                ['notify_milestone_complete', 'Milestone complete'],
                ['notify_ai_insight', 'Critical AI insight'],
              ] as [keyof TeamsFormState, string][]).map(([key, label]) => (
                <div key={key} className="flex items-center gap-3">
                  <Switch id={`teams-${key}`} checked={Boolean(teamsForm[key])} onCheckedChange={v => setTeamsForm(f => ({ ...f, [key]: v }))} />
                  <Label htmlFor={`teams-${key}`}>{label}</Label>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamsDialog(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleCreateTeams} disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" aria-hidden />}
              Connect Teams
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Jira dialog */}
      <Dialog open={showJiraDialog} onOpenChange={setShowJiraDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Puzzle className="size-5" aria-hidden /> Add Jira Integration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Name" id="jira-name">
              <Input id="jira-name" value={jiraForm.name} onChange={e => setJiraForm(f => ({ ...f, name: e.target.value }))} className="min-h-[44px]" />
            </Field>
            <Field label="Jira URL" id="jira-url" hint="Your Jira instance, e.g. https://myorg.atlassian.net">
              <Input id="jira-url" value={jiraForm.jira_url} onChange={e => setJiraForm(f => ({ ...f, jira_url: e.target.value }))} placeholder="https://myorg.atlassian.net" className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Email" id="jira-email" hint="Your Atlassian account email">
              <Input id="jira-email" type="email" value={jiraForm.email} onChange={e => setJiraForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="API Token" id="jira-token" hint="From id.atlassian.com → Security → API tokens">
              <Input id="jira-token" type="password" value={jiraForm.api_token} onChange={e => setJiraForm(f => ({ ...f, api_token: e.target.value }))} className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Project Key" id="jira-project-key" hint="e.g. PROJ — required for two-way sync">
              <Input id="jira-project-key" value={jiraForm.project_key} onChange={e => setJiraForm(f => ({ ...f, project_key: e.target.value }))} placeholder="PROJ" className="min-h-[44px]" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJiraDialog(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleCreateJira} disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" aria-hidden />}
              Connect Jira
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asana dialog */}
      <Dialog open={showAsanaDialog} onOpenChange={setShowAsanaDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="size-5" aria-hidden /> Add Asana Integration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <Field label="Name" id="asana-name">
              <Input id="asana-name" value={asanaForm.name} onChange={e => setAsanaForm(f => ({ ...f, name: e.target.value }))} className="min-h-[44px]" />
            </Field>
            <Field label="Personal access token" id="asana-token" hint="From Asana → My settings → Apps → Developer apps">
              <Input id="asana-token" type="password" value={asanaForm.access_token} onChange={e => setAsanaForm(f => ({ ...f, access_token: e.target.value }))} className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Project ID" id="asana-project" hint="The number in the Asana project URL">
              <Input id="asana-project" inputMode="numeric" value={asanaForm.project_gid} onChange={e => setAsanaForm(f => ({ ...f, project_gid: e.target.value }))} placeholder="1200000000000000" className="min-h-[44px]" aria-required="true" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAsanaDialog(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleCreateAsana} disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" aria-hidden />}
              Connect Asana
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GitHub dialog */}
      <Dialog open={showGitHubDialog} onOpenChange={setShowGitHubDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Github className="size-5" aria-hidden /> Add GitHub Integration
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Repository owner" id="gh-owner">
              <Input id="gh-owner" value={githubForm.repo_owner} onChange={e => setGitHubForm(f => ({ ...f, repo_owner: e.target.value }))} placeholder="my-org" className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Repository name" id="gh-repo">
              <Input id="gh-repo" value={githubForm.repo_name} onChange={e => setGitHubForm(f => ({ ...f, repo_name: e.target.value }))} placeholder="my-repo" className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Webhook secret" id="gh-secret" hint="Set the same value in GitHub repo settings → Webhooks">
              <Input id="gh-secret" type="password" value={githubForm.webhook_secret} onChange={e => setGitHubForm(f => ({ ...f, webhook_secret: e.target.value }))} className="min-h-[44px]" aria-required="true" />
            </Field>
            <Field label="Project ID (leave blank for org-wide)" id="gh-project">
              <Input id="gh-project" type="number" value={githubForm.project_id} onChange={e => setGitHubForm(f => ({ ...f, project_id: e.target.value }))} placeholder="Optional" className="min-h-[44px]" />
            </Field>
            <div className="flex items-center gap-3">
              <Switch id="gh-auto-close" checked={githubForm.auto_close_on_merge} onCheckedChange={v => setGitHubForm(f => ({ ...f, auto_close_on_merge: v }))} />
              <Label htmlFor="gh-auto-close">Auto-set task to Done when PR is merged</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGitHubDialog(false)} className="min-h-[44px]">Cancel</Button>
            <Button onClick={handleCreateGitHub} disabled={saving} className="min-h-[44px]">
              {saving && <Loader2 className="size-4 animate-spin mr-1" aria-hidden />}
              Connect GitHub
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

function Field({ label, id, hint, children }: { label: string; id: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground mb-1">{hint}</p>}
      <div className="mt-1">{children}</div>
    </div>
  )
}

function IntegrationRow({
  integration,
  onToggle,
  onDelete,
  extraAction,
}: {
  integration: Integration
  onToggle: () => void
  onDelete: () => void
  extraAction?: React.ReactNode
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg border bg-card p-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm truncate">{integration.name}</span>
          <Badge variant={integration.is_active ? 'default' : 'secondary'} className="text-xs shrink-0">
            {integration.is_active ? (
              <><CheckCircle2 className="size-3 mr-1" aria-hidden /> Active</>
            ) : (
              <><AlertCircle className="size-3 mr-1" aria-hidden /> Inactive</>
            )}
          </Badge>
          {integration.project_id && (
            <Badge variant="outline" className="text-xs shrink-0">Project {integration.project_id}</Badge>
          )}
        </div>
        {integration.last_sync_at && (
          <p className="text-xs text-muted-foreground mt-0.5">
            Last sync: {new Date(integration.last_sync_at).toLocaleString()}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {extraAction}
        <Switch checked={integration.is_active} onCheckedChange={onToggle} aria-label={`${integration.is_active ? 'Disable' : 'Enable'} ${integration.name}`} />
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive hover:bg-destructive/10 min-h-[44px] min-w-[44px]"
          onClick={onDelete}
          aria-label={`Remove ${integration.name} integration`}
        >
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>
    </li>
  )
}
